import { useState, useEffect } from 'react';
import axios from 'axios';
import {
    FileText, Upload, Download, Trash2, Loader2,
    File, FileCode, FileImage, FileSpreadsheet, MoreVertical, Search
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { UploadDocumentModal } from './UploadDocumentModal';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from './ui/alert-dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOCUMENT_TYPES = {
    contract: 'Contrato',
    power: 'Poder',
    invoice: 'Factura',
    correspondence: 'Correspondencia',
    legal: 'Legal',
    other: 'Otro'
};

const DOCUMENT_TYPE_COLORS = {
    contract: 'bg-blue-100 text-blue-700',
    power: 'bg-purple-100 text-purple-700',
    invoice: 'bg-green-100 text-green-700',
    correspondence: 'bg-yellow-100 text-yellow-700',
    legal: 'bg-red-100 text-red-700',
    other: 'bg-gray-100 text-gray-700'
};

export const ClientDocuments = ({ clientId, entityType = 'client', entityId, token, folderId, trademarks }) => {
    // Use entityId if provided, otherwise default to clientId for client entity
    const targetId = entityId || clientId;

    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Upload modal state
    const [uploadModalOpen, setUploadModalOpen] = useState(false);

    // Delete modal state
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [docToDelete, setDocToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (targetId) {
            fetchDocuments();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, entityType]);

    async function fetchDocuments() {
        try {
            const res = await axios.get(`${API}/documents`, {
                params: {
                    entity_type: entityType,
                    entity_id: targetId
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            setDocuments(res.data);
        } catch (error) {
            console.error('Error fetching documents:', error);
            toast.error('Error al cargar documentos');
        } finally {
            setLoading(false);
        }
    }



    const handleDelete = async () => {
        if (!docToDelete) return;

        setDeleting(true);
        try {
            await axios.delete(`${API}/documents/${docToDelete.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success('Documento eliminado');
            await fetchDocuments();
        } catch (error) {
            console.error('Error deleting document:', error);
            toast.error('Error al eliminar documento');
        } finally {
            setDeleting(false);
            setDeleteDialogOpen(false);
            setDocToDelete(null);
        }
    };

    const handleDownload = async (doc) => {
        try {
            // Direct download via window.open using the token query param if needed
            // Or blob download for better security
            const response = await axios.get(`${API}/documents/${doc.id}/download`, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', doc.original_filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Download error:', error);
            toast.error('Error al descargar el archivo');
        }
    };

    const getFileIcon = (mimeType) => {
        if (mimeType.includes('image')) return <FileImage className="h-5 w-5 text-purple-500" />;
        if (mimeType.includes('pdf')) return <FileText className="h-5 w-5 text-red-500" />;
        if (mimeType.includes('spreadsheet') || mimeType.includes('excel')) return <FileSpreadsheet className="h-5 w-5 text-green-500" />;
        if (mimeType.includes('text') || mimeType.includes('code')) return <FileCode className="h-5 w-5 text-blue-500" />;
        return <File className="h-5 w-5 text-gray-400" />;
    };

    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Filter documents
    const filteredDocuments = documents.filter(doc => {
        // 1. Text Search
        const matchesSearch = doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            doc.original_filename.toLowerCase().includes(searchTerm.toLowerCase());

        if (!matchesSearch) return false;

        // 2. Portfolio Scope (Frontend filtering)
        if (folderId) {
            // Client documents: Only show in "Todas" (so hide if folder selected)
            if (doc.entity_type === 'client') return false;

            // Trademark documents: Check if trademark belongs to folder
            if (doc.entity_type === 'trademark') {
                const tm = trademarks?.find(t => t.id === doc.entity_id);
                // IF trademark known and not in folder -> hide
                // IF trademark unknown (maybe archived?) -> hide
                if (!tm || (tm.folder_id !== folderId && tm.portfolio_id !== folderId)) return false;
            }

            // Procedure documents: Ideally we check linked trademarks. 
            // For MVP client-side, we might not have procedure data here.
            // We'll hide them to be strict with scope, or show if unsure. 
            // Decision: Hide to enforce scope, prevent clutter.
            if (doc.entity_type === 'procedure') return false;
        }

        return true;
    });

    if (loading) {
        return (
            <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
            </div>
        );
    }

    return (
        <div className="bg-white rounded-lg border border-gray-200">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        Documentos ({documents.length})
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Gestión de archivos y contratos
                    </p>
                </div>
                <Button onClick={() => setUploadModalOpen(true)} size="sm" className="bg-gray-900 hover:bg-gray-800">
                    <Upload className="h-4 w-4 mr-2" />
                    Subir Documento
                </Button>
            </div>

            {/* Toolbar */}
            {documents.length > 0 && (
                <div className="px-6 py-3 border-b border-gray-200 bg-gray-50 flex items-center gap-4">
                    <div className="relative max-w-sm w-full">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
                        <Input
                            placeholder="Buscar documentos..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 h-9 bg-white"
                        />
                    </div>
                </div>
            )}

            {/* Documents List */}
            {documents.length === 0 ? (
                <div className="p-12 text-center">
                    <Upload className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 mb-2">No hay documentos</p>
                    <p className="text-sm text-gray-400 mb-6">Sube documentos importantes aquí</p>
                    <Button onClick={() => setUploadModalOpen(true)} variant="outline">
                        Subir primer documento
                    </Button>
                </div>
            ) : filteredDocuments.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                    No se encontraron documentos con "{searchTerm}"
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10">
                                    Tipo
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Nombre
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Detalles
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Fecha
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {filteredDocuments.map(doc => (
                                <tr key={doc.id} className="hover:bg-gray-50 group">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {getFileIcon(doc.mime_type)}
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-medium text-gray-900 group-hover:text-blue-600 cursor-pointer" onClick={() => handleDownload(doc)}>
                                                {doc.name}
                                            </span>
                                            {doc.description && (
                                                <span className="text-xs text-gray-500 truncate max-w-xs">
                                                    {doc.description}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col gap-1">
                                            <Badge className={`${DOCUMENT_TYPE_COLORS[doc.document_type]} border-0 text-xs w-fit`}>
                                                {DOCUMENT_TYPES[doc.document_type]}
                                            </Badge>
                                            <span className="text-xs text-gray-400">
                                                {formatFileSize(doc.file_size)}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex flex-col">
                                            <span className="text-sm text-gray-900">
                                                {new Date(doc.created_at).toLocaleDateString()}
                                            </span>
                                            <span className="text-xs text-gray-400">
                                                por {doc.uploaded_by_user_name || 'Usuario'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                    <MoreVertical className="h-4 w-4 text-gray-500" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end">
                                                <DropdownMenuItem onClick={() => handleDownload(doc)}>
                                                    <Download className="mr-2 h-4 w-4" />
                                                    Descargar
                                                </DropdownMenuItem>
                                                <DropdownMenuItem
                                                    onClick={() => { setDocToDelete(doc); setDeleteDialogOpen(true); }}
                                                    className="text-red-600 focus:text-red-600 focus:bg-red-50"
                                                >
                                                    <Trash2 className="mr-2 h-4 w-4" />
                                                    Eliminar
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Upload Modal */}
            <UploadDocumentModal
                open={uploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                token={token}
                entityId={targetId}
                entityType={entityType}
                onUploadComplete={fetchDocuments}
            />

            {/* Delete Dialog */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>¿Eliminar documento?</AlertDialogTitle>
                        <AlertDialogDescription>
                            El documento "{docToDelete?.name}" será eliminado permanentemente. Esta acción no se puede deshacer.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
                            {deleting ? 'Eliminando...' : 'Eliminar'}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};
